import React, { useState } from 'react';
import * as dates from '@simplybenefits/sb-dates';
import Modal from './Modal.js';
import Text from '../Text.js';
import { DateInput } from '../../index.js';
import i18n from '../../localization/i18n.js';
import tokens from '../../localization/tokens.js';
import { validateInput } from '../../services/index.js';

/**
 * Component for removing an employee by setting an effective termination date.
 * 
 * Props:
 * - open: Boolean indicating whether the modal is open.
 * - close: Function to close the modal.
 * - terminate: Function to handle the termination process.
 * - currentEmployee: Object containing the current employee's details.
 * - loader: Boolean indicating if a loader should be shown.
 * 
 * @returns A modal component for confirming employee termination.
 */
const TerminateEmployeeModal = ({
  open,
  close,
  terminate,
  currentEmployee,
  loader
}) => {
  const [effectiveDate, setEffectiveDate] = useState(null);
  const [effectiveDateError, setEffectiveDateError] = useState(false);

  /**
   * Handles the confirmation of employee termination.
   * Validates the effective date and initiates the termination process.
   */
  const confirmTermination = async () => {
    const effectiveDateValidation = await validateInput('effectiveDate', effectiveDate);
    if (effectiveDateValidation) {
      setEffectiveDateError(effectiveDateValidation);
      return false;
    }
    setEffectiveDateError(false);
    const success = await terminate(currentEmployee.userID, currentEmployee.accountID, dates.getUTC(effectiveDate));
    if (success) {
      close();
      return true;
    }
    return false;
  };

  /**
   * Resets the form state to its initial values.
   * Clears the effective date and any validation errors.
   */
  const resetForm = () => {
    setEffectiveDate(null);
    setEffectiveDateError(false);
  };

  /**
   * Handles closing of the modal.
   * Resets the form and triggers the close function passed via props.
   */
  const handleClose = () => {
    resetForm();
    close();
  };
  return /*#__PURE__*/React.createElement(Modal, {
    open: open,
    onClose: handleClose,
    title: i18n.t(tokens.titles.setEffectiveDate),
    primaryButton: {
      label: i18n.t(tokens.buttons.terminate),
      onClick: confirmTermination
    },
    loader: loader
  }, /*#__PURE__*/React.createElement(Text, {
    medium: true
  }, i18n.t(tokens.labels.employees.effectiveDateOfTermination)), /*#__PURE__*/React.createElement(DateInput, {
    label: i18n.t(tokens.labels.effectiveDate),
    value: effectiveDate,
    onChange: e => setEffectiveDate(e.target.value),
    error: Boolean(effectiveDateError),
    helperText: effectiveDateError,
    fullWidth: true,
    required: true
  }), /*#__PURE__*/React.createElement(Text, {
    medium: true,
    margin: "10px 0 0 0"
  }, i18n.t(tokens.labels.employees.byClickingThe), " ", /*#__PURE__*/React.createElement(Text, {
    customStyle: {
      display: 'inline'
    },
    medium: true,
    bold: true
  }, i18n.t(tokens.labels.employees.terminate)), " button below, the termination for ", /*#__PURE__*/React.createElement(Text, {
    customStyle: {
      display: 'inline'
    },
    medium: true,
    bold: true
  }, `${currentEmployee?.firstname} ${currentEmployee?.lastname}`), " ", i18n.t(tokens.labels.employees.willBeAppliedOnEffectiveDate)));
};
export default TerminateEmployeeModal;